import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produtoService, Produto } from '../../services/produto';
import SelectProdutoUnificar from './SelectProdutoUnificar';

const UnificarProduto: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [idProdutoUnificar, setIdProdutoUnificar] = useState<number | null>(null);
  const [errors, setErrors] = useState({ produtoUnificar: '' });

  // Buscar produto atual
  const { data: produto, isLoading, error } = useQuery({
    queryKey: ['produto', id],
    queryFn: () => produtoService.getById(Number(id)),
    enabled: !!id,
  });

  // Mutação para unificar
  const unificarMutation = useMutation({
    mutationFn: ({ idPermanecer, idExcluir }: { idPermanecer: number; idExcluir: number }) =>
      produtoService.unificar(idPermanecer, idExcluir),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      navigate('/produtos');
    },
    onError: (error: any) => {
      console.error('Erro ao unificar produtos:', error);
    },
  });

  useEffect(() => {
    if (idProdutoUnificar) {
      setErrors(prev => ({ ...prev, produtoUnificar: '' }));
    }
  }, [idProdutoUnificar]);

  const handleProdutoUnificarChange = (value: number) => {
    setIdProdutoUnificar(value);
  };

  const validateForm = () => {
    const newErrors = { produtoUnificar: '' };
    let isValid = true;

    if (!idProdutoUnificar) {
      newErrors.produtoUnificar = 'Selecione um produto para unificar';
      isValid = false;
    }

    if (idProdutoUnificar === Number(id)) {
      newErrors.produtoUnificar = 'Não é possível unificar o produto com ele mesmo';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (validateForm()) {
      unificarMutation.mutate({
        idPermanecer: Number(id),
        idExcluir: idProdutoUnificar!,
      });
    }
  };

  const handleBack = () => {
    navigate('/produtos');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !produto) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Erro ao carregar os dados do produto.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Unificar Produto
          </Typography>
        </Box>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          {/* Produto que permanecerá */}
          <Card 
            variant="outlined" 
            sx={{ 
              backgroundColor: '#f8fff8', 
              border: '2px solid',
              borderColor: 'success.main',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.dark', fontSize: '1.4rem' }}>
                <CompareArrowsIcon sx={{ mr: 1, fontSize: '1.8rem', verticalAlign: 'middle' }} />
                Produto que Permanecerá
              </Typography>
              <Divider sx={{ mb: 3, borderColor: 'success.main' }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                    Código
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1.2rem' }}>
                    #{produto.id}
                  </Typography>
                </Box>
                
                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                    Nome do Produto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '1.1rem' }}>
                    {produto.nomeProduto}
                  </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                    Referência
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '1.1rem', color: 'primary.main' }}>
                    {produto.referencia}
                  </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                    Preço de Venda
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.dark', fontSize: '1.2rem' }}>
                    {formatCurrency(produto.precoVenda)}
                  </Typography>
                </Box>

                <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                    Descrição
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '1rem' }}>
                    {produto.descricao}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Chip
                    label="ESTE PRODUTO SERÁ MANTIDO"
                    color="success"
                    size="medium"
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.9rem',
                      px: 2,
                      py: 1
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Produto que será excluído */}
          <Card 
            variant="outlined" 
            sx={{ 
              backgroundColor: '#fff8f8', 
              border: '2px solid',
              borderColor: 'error.main',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'error.dark', fontSize: '1.4rem' }}>
                <CompareArrowsIcon sx={{ mr: 1, fontSize: '1.8rem', verticalAlign: 'middle' }} />
                Produto que será Excluído
              </Typography>
              <Divider sx={{ mb: 3, borderColor: 'error.main' }} />
              
              <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem', mb: 1 }}>
                  Selecione o produto para unificar
                </Typography>
                <SelectProdutoUnificar
                  value={idProdutoUnificar || undefined}
                  onChange={handleProdutoUnificarChange}
                  error={!!errors.produtoUnificar}
                  helperText={errors.produtoUnificar}
                  excludeId={Number(id)}
                  required
                />
              </Box>

              {idProdutoUnificar && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Chip
                    label="ESTE PRODUTO SERÁ EXCLUÍDO"
                    color="error"
                    size="medium"
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.9rem',
                      px: 2,
                      py: 1
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Alerta de confirmação */}
        {idProdutoUnificar && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Atenção: Esta operação não pode ser desfeita!
            </Typography>
            <Typography variant="body2">
              O produto selecionado será permanentemente excluído e todos os seus dados serão consolidados no produto que permanecerá.
            </Typography>
          </Alert>
        )}

        {/* Botões de ação */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={unificarMutation.isPending || !idProdutoUnificar}
            sx={{ minWidth: 120 }}
          >
            {unificarMutation.isPending ? 'Unificando...' : 'Confirmar Unificação'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Cancelar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default UnificarProduto;
